// "Next invoice" on the invoice page. Opened from the invoice list, it follows exactly what the list showed —
// its filters, view, search and sort, across all pages (the list hands its order over in the navigation state,
// like the patient list does for "next patient"). Opened any other way, it falls back to newest first.

type Person = { firstName?: string; lastName?: string } | null | undefined;

export type InvoiceNavRow = { id: string; readableId?: string; name: string };

const personName = (p: Person) => [p?.firstName, p?.lastName].filter(Boolean).join(" ");

/** Who an invoice is for — the patient, else the payer, else its number. */
export const invoiceNavName = (inv: { readableId?: string; patient?: Person; recipient?: Person }) =>
    personName(inv.patient) || personName(inv.recipient) || inv.readableId || "";

/** The list's current order, small enough to carry in the navigation state. */
export const invoiceNavRows = (list: { id: string; readableId?: string; patient?: Person; recipient?: Person }[]): InvoiceNavRow[] =>
    list.map((inv) => ({ id: inv.id, readableId: inv.readableId, name: invoiceNavName(inv) }));

/** Newest first (creation date), the order when the page wasn't opened from the list. */
export const newestFirst = <T extends { creationDate?: string | Date; readableId?: string }>(list: T[]): T[] =>
    [...list].sort((a, b) =>
        String(b.creationDate ?? "").localeCompare(String(a.creationDate ?? "")) || String(a.readableId ?? "").localeCompare(String(b.readableId ?? "")),
    );

/** The row after `currentId`, wrapping around at the end — null when the invoice isn't in the rows or it's alone. */
export function nextInvoiceRow(rows: InvoiceNavRow[], currentId: string | undefined): InvoiceNavRow | null {
    if (!currentId || rows.length < 2) return null;
    const idx = rows.findIndex((r) => r.id === currentId);
    return idx < 0 ? null : rows[(idx + 1) % rows.length];
}
